import { useState, useEffect } from "react";
import { Modal, Button, Form, Table } from "react-bootstrap";
import { fetchOrderById, updateOrderStatus, generateDtdcOrder, cancelDtdcOrder, fetchDtdcLabel, fetchDtdcTracking, updateOrderItemProductSize } from "../../actions/product";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { formatDateTime } from "../../services/helper";
import UpdateOrderAddressModal from "./UpdateOrderAddressModal";

const OrderDetails = ({ orderId, onHide, onOrderUpdate, hideLoading, orderStatusList }) => {
  const { role } = useSelector((state) => state.auth.user);
  const [order, setOrder] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // const [isFullyDelivered, setIsFullyDelivered] = useState(false);
  const [error, setError] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [trackingData, setTrackingData] = useState(null);
  const [fetchingTracking, setFetchingTracking] = useState(false);
  const [editingSizeId, setEditingSizeId] = useState(null);
  const [statusNote, setStatusNote] = useState("");
  const [showAddressModal, setShowAddressModal] = useState(false);

  useEffect(() => {
    const fetchDetails = async () => {
      if (!orderId) return;

      setLoading(true);
      setError(null);
      try {
          const fetchedOrder = await fetchOrderById(orderId);
          setOrder(fetchedOrder);

          if (!fetchedOrder) {
            setError("Order not found.");
            return;
          }

          setNewStatus(fetchedOrder.status);
          setStatusNote(fetchedOrder.note);

          if (fetchedOrder && fetchedOrder.items) {
            const allProductIds = [];
            const studentProductMap = {};
            

          const productsWithStatus = fetchedOrder.items.flatMap(item => 
              (item.orderItemProducts || [])
              .filter(orderItems => Number(orderItems.totalQuantity) > 0)
              .map(orderItems => {
                  let productName = orderItems.product?.name || "";

                  if (
                    orderItems.product?.display_name === "Sports V Collar T-Shirt" ||
                    orderItems.product?.name ===
                      "TGS Uni V Collar Slate HD Tee - 4 Colors" ||
                    orderItems.product?.name === "Sports CC T-Shirt" ||
                    orderItems.product?.name === "TGS Uni CC Slate Tee - 4 Colors"
                  ) {
                    const house = item.student?.house;
                    if (house === "Dragons") {
                      productName = productName.replace("4 Colors", "Red");
                    }
                    if (house === "Pegasus") {
                      productName = productName.replace("4 Colors", "Blue");
                    }
                    if (house === "Sphinx") {
                      productName = productName.replace("4 Colors", "Yellow");
                    }
                    if (house === "Griffins") {
                      productName = productName.replace("4 Colors", "Green");
                    }
                  }

                  return {
                      ...orderItems,
                      name: productName || orderItems.product?.name,
                      availableSizes: orderItems.product?.availableSizes,
                      bundleName: item.bundle?.name,
                      isIndividualProduct: item.bundle?.isIndividualProduct,
                      size: orderItems.size,
                      weight: orderItems.weight ?? orderItems.product?.weight ?? 0,
                      studentName: item.student?.studentName,
                      studentId: item.student?.usid,
                      bundleId: item.bundle?.id,
                      orderItemId: item.id,
                      delivered: orderItems.status === 'ARRIVED_AT_WAREHOUSE' ? true :false,
                      note: orderItems.note ||''
                  };
              })
          );
          setProducts(productsWithStatus);
          
          // const allProductsDelivered = productsWithStatus.every(p => p.delivered);
          // setIsFullyDelivered(allProductsDelivered);
        }
      } catch (err) {
        setError("Failed to fetch order details. Please try again.");
        console.error("Error fetching details:", err);
      } finally {
        setLoading(false);
        hideLoading()
      }
    };

    fetchDetails();
  }, [orderId]);


  const handleSizeUpdate = async (orderItemProductId, newSize) => {
    setSaving(true);
    try {
      await updateOrderItemProductSize(orderItemProductId, newSize);
      setProducts(prev => prev.map(p => p.id === orderItemProductId ? { ...p, size: newSize } : p));
      setEditingSizeId(null);
      // toast.success("Order Product size updated successfully!");
    } catch (error) {
      toast.error("Failed to update size.");
    } finally {
      setSaving(false);
    }
  };

  const handleStatusUpdate = async () => {
    setSaving(true);
    try {
      if(newStatus){
        const payload = { status: newStatus, note: statusNote };
        if (newStatus === "HOLD_AT_WAREHOUSE") {
          payload.items = products.map(p => ({
            productId: p.productId,
            orderItemId: p.orderItemId,
            bundleId : p.bundleId,
            studentId : p.studentId,
            delivered: p.delivered,
            note: p.note
          }));
        }
        await updateOrderStatus(orderId, payload);
        toast.success("Order Status updated successfully!");
        setStatusNote("");
        onOrderUpdate();
      }else{
        toast.error("Please Select Status!");
      }

    } catch (error) {
      toast.error("Failed to update order.");
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateDtdc = async () => {
    setSaving(true);
    try {
      await generateDtdcOrder(orderId);
      const updatedOrder = await fetchOrderById(orderId);
      setOrder(updatedOrder);
      onOrderUpdate();
    } catch (error) {
      console.error("Error generating DTDC order:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelDtdc = async () => {
    if (!window.confirm("Are you sure you want to cancel this DTDC order?")) return;
    
    setSaving(true);
    try {
      await cancelDtdcOrder(orderId);
      const updatedOrder = await fetchOrderById(orderId);
      if (updatedOrder) {
        setOrder(updatedOrder);
        onOrderUpdate();
      }
    } catch (error) {
      console.error("Error cancelling DTDC order:", error);
    } finally {
      setSaving(false);
    }
  };

  const [showLabelModal, setShowLabelModal] = useState(false);
  const labelOptions = [
    { label: "Shipping Label A4", value: "SHIP_LABEL_A4" },
    { label: "Shipping Label A6", value: "SHIP_LABEL_A6" },
    { label: "Shipping Label POD", value: "SHIP_LABEL_POD" },
    { label: "Shipping Label 4x6", value: "SHIP_LABEL_4X6" },
    { label: "Routing Label A4", value: "ROUTE_LABEL_A4" },
    { label: "Routing Label 4x4", value: "ROUTE_LABEL_4X4" }
  ];

  const handlePrintLabelClick = () => {
    setShowLabelModal(true);
  };

  const handlePrintLabel = async (labelCode) => {
    setShowLabelModal(false);
    setSaving(true);
    try {
      const response = await fetchDtdcLabel(orderId, labelCode);
      const blob = new Blob([response], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      if (response && url) {
        window.open(url, '_blank');
      } else {
        toast.error("Label URL not found");
      }
    } catch (error) {
      console.error("Error fetching DTDC label:", error);
      toast.error("Failed to fetch DTDC label");
    } finally {
      setSaving(false);
    }
  };

  const handleViewTracking = async () => {
    setFetchingTracking(true);
    try {
      const data = await fetchDtdcTracking(orderId);
      setTrackingData(data);
      setShowTrackingModal(true);
    } catch (error) {
      console.error("Error fetching tracking details:", error);
    } finally {
      setFetchingTracking(false);
    }
  };

  const renderBody = () => {
    if (loading) {
        return <p>Loading...</p>;
    }

    if (error) {
        return <p className="text-danger">{error}</p>;
    }

    if (!order) {
        return null;
    }

    return (
      <div className="table-responsive border rounded shadow-sm bg-white">
        <table className="table table-hover align-middle mb-0">
          <thead className="table-light">
            <tr>
              <th className="small fw-bold text-muted text-uppercase px-3">Student</th>
              <th className="small fw-bold text-muted text-uppercase">Bundle / Type</th>
              <th className="small fw-bold text-muted text-uppercase">Product</th>
              <th className="small fw-bold text-muted text-uppercase">Size</th>
              <th className="small fw-bold text-muted text-uppercase text-center">Qty</th>
              {newStatus === "HOLD_AT_WAREHOUSE" && (
                <>
                  <th className="small fw-bold text-muted text-uppercase text-center">Received</th>
                  <th className="small fw-bold text-muted text-uppercase">Note</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {products.map((product, index) => (
              <tr key={index} className={product.delivered === "DELIVERED" ? "table-success bg-opacity-10" : ""}>
                <td className="px-3">
                  <div className="fw-bold text-dark">{product.studentName}</div>
                  <div className="text-muted small">USID: {product.studentId}</div>
                </td>

                <td>
                  <div className="mb-1">
                    <span className={`badge ${product.isIndividualProduct ? 'bg-info text-white' : 'bg-warning text-dark'}`}>
                      {product.isIndividualProduct ? 'Single Product' : 'Bundle'}
                    </span>
                  </div>
                  <div className="small fw-semibold">{product.bundleName}</div>
                </td>

                <td>
                  <div className="small fw-semibold">{product.name}</div>
                  <div className="text-muted" style={{ fontSize: '0.75rem' }}>ID: {product.productId}</div>
                </td>

                <td>
                  {editingSizeId === product.id ? (
                    <Form.Select
                      size="sm"
                      value={product.size}
                      onChange={(e) => handleSizeUpdate(product.id, e.target.value)}
                      onBlur={() => setEditingSizeId(null)}
                      autoFocus
                    >
                      <option value={product.size}>{product.size}</option>
                      {product.availableSizes?.filter(s => s !== product.size).map(size => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </Form.Select>
                  ) : (
                    <span 
                      className="badge bg-secondary rounded-pill" 
                      style={{ cursor: (role === 'superadmin' || role === 'admin') ? 'pointer' : 'default' }}
                      onClick={() => (role === 'superadmin' || role === 'admin') && setEditingSizeId(product.id)}
                    >
                      {product.size || 'NA'}
                    </span>
                  )}
                </td>
                <td className="text-center">
                  <span className="badge bg-light text-dark border fw-bold px-3">{product.totalQuantity}</span>
                </td>
                {newStatus === "HOLD_AT_WAREHOUSE" && (
                  <>
                    <td className="text-center">
                      <Form.Check 
                        type="switch"
                        id={`received-switch-${index}`}
                        checked={product.delivered}
                        onChange={(e) => {
                          const val = e.target.checked;
                          setProducts(prev => prev.map((p, i) => i === index ? { ...p, delivered: val, note: val ? "" : p.note } : p));
                        }}
                      />
                    </td>
                    <td>
                      {!product.delivered && (
                        <Form.Control 
                          size="sm"
                          type="text"
                          placeholder="Add note..."
                          value={product.note}
                          onChange={(e) => {
                            const val = e.target.value;
                            setProducts(prev => prev.map((p, i) => i === index ? { ...p, note: val } : p));
                          }}
                        />
                      )}
                    </td>
                  </>
                )}
              </tr>
            ))}
            <tr className="bg-light">
              <td colSpan={newStatus === "HOLD_AT_WAREHOUSE" ? "7" : "5"} className="p-3">
                <div className="row align-items-center">
                  <div className="col-md-6">
                    <label className="form-label fw-bold small text-muted text-uppercase mb-2 d-block">
                      <i className="bi bi-arrow-repeat me-2"></i>Update Order Delivery Status
                    </label>
                    <Form.Select
                      size="sm"
                      value={newStatus}
                      onChange={(e) => setNewStatus(e.target.value)}
                      className="shadow-sm fw-bold border-primary"
                    >
                      <option value={order.status}>Current: {order.status.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}</option>
                      {(() => {
                        const statusSequence = [
                          { key: "IN_PROGRESS", order: 1, value: "IN_PROGRESS" },
                          { key: "ARRIVED_AT_WAREHOUSE", order: 2, value: "ARRIVED_AT_WAREHOUSE" },
                          { key: "HOLD_AT_WAREHOUSE", order: 3, value: "HOLD_AT_WAREHOUSE" },
                          { key: "PACKED", order: 4, value: "PACKED" },
                          { key: "SHIPPED", order: 5, value: "SHIPPED" },
                          { key: "OUT_FOR_DELIVERY", order: 6, value: "OUT_FOR_DELIVERY" },
                          { key: "DELIVERY_IN_PROGRESS", order: 7, value: "DELIVERY_IN_PROGRESS" },
                          { key: "ATTEMPTED", order: 8, value: "ATTEMPTED" },
                          { key: "HELDUP", order: 9, value: "HELDUP" },
                          { key: "RTO", order: 10, value: "RTO" },
                          // { key: "PARTIALLY_DELIVERED", order: 11, value: "PARTIALLY_DELIVERED" },
                          { key: "DELIVERED", order: 12, value: "DELIVERED" },
                        ];
                        const currentStatusObj = statusSequence.find(s => s.key === order.status);
                        const currentOrder = currentStatusObj ? currentStatusObj.order : 0;
                        return orderStatusList
                          .filter(s => {
                            const targetObj = statusSequence.find(seq => seq.key === s.name);
                            if (role === 'superadmin') return !!targetObj;
                            return targetObj && targetObj.order > currentOrder;
                          })
                          .sort((a, b) => {
                            const orderA = statusSequence.find(seq => seq.key === a.name)?.order || 0;
                            const orderB = statusSequence.find(seq => seq.key === b.name)?.order || 0;
                            return orderA - orderB;
                          })
                          .map(status => (
                            <option key={status.name} value={status.name}>
                              {status.displayName}
                            </option>
                          ));
                      })()}
                    </Form.Select>
                    <div className="mt-2">
                      <Form.Control 
                        as="textarea" 
                        rows={2} 
                        size="sm" 
                        placeholder="Optional note for status update" 
                        value={statusNote} 
                        onChange={(e) => setStatusNote(e.target.value)} 
                      />
                    </div>
                  </div>
                  <div className="col-md-6 text-end pt-3 pt-md-0">
                    <small className="text-muted d-block mb-1">Last updated on</small>
                    <span className="fw-bold small">{formatDateTime(order.updatedAt)}</span>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );

  };

  return (
    <Modal show={true} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center flex-wrap gap-2">
          <span>Order Details - {orderId}</span>
          {order?.customerCode && (
            <span className="badge bg-secondary bg-opacity-25 text-dark border fs-6 fw-normal px-2 py-1">
              DTDC: {order.customerCode}
            </span>
          )}
          {order?.totalWeight > 0 && (
            <span className="badge bg-info bg-opacity-10 text-primary border border-info border-opacity-25 fs-6 fw-normal px-2 py-1">
              <i className="bi bi-box-seam me-1"></i>Weight: {order.totalWeight} KG
            </span>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {order && (
          <div className="border rounded p-3 mb-3 bg-light bg-opacity-50">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="fw-bold text-dark mb-0 small text-uppercase">
                <i className="bi bi-geo-alt-fill text-danger me-1"></i>Delivery Address
                {order.isAddressEdited && (
                  <span className="badge bg-info bg-opacity-25 text-primary border ms-2">Edited</span>
                )}
              </h6>
              {role === "superadmin" && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="py-0 px-2"
                  style={{ fontSize: "11px" }}
                  onClick={() => setShowAddressModal(true)}
                >
                  <i className="bi bi-pencil-square me-1"></i>Edit Address
                </Button>
              )}
            </div>
            <div className="small">
              <p className="mb-1 text-dark fw-semibold">
                {order.deliveryAddress || order.parent?.address || "Campus Pick-up"}
              </p>
              <p className="mb-1 text-muted">
                <span className="me-3">
                  <strong>City:</strong> {order.city || order.parent?.city || "Hyderabad"}
                </span>
                <span className="me-3">
                  <strong>State:</strong> {order.state || order.parent?.state || "Telangana"}
                </span>
                <span>
                  <strong>Pincode:</strong> {order.pincode || order.parent?.pincode || "--"}
                </span>
              </p>
              <p className="mb-0 text-muted">
                <i className="bi bi-telephone me-1"></i>Contact: {order.deliveryContact || order.parent?.phoneNumber || "--"}
              </p>
            </div>
          </div>
        )}
        {renderBody()}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={saving}>
          Close
        </Button>
        
        {order?.status === "PACKED" && !order?.trackingId && (
          <Button variant="warning" onClick={handleGenerateDtdc} disabled={saving}>
            {saving ? "Generating..." : "Generate DTDC Order"}
          </Button>
        )}

        {order?.trackingId && order?.status !== "CANCELLED" && order?.status !== "DELIVERED" && order?.status !== "RTO" && (
          <Button variant="danger" onClick={handleCancelDtdc} disabled={saving}>
            {saving ? "Cancelling..." : "Cancel DTDC Order"}
          </Button>
        )}
        {order?.trackingId && order?.status !== "CANCELLED" && order?.status !== "DELIVERED" && (
          <Button variant="info" onClick={handlePrintLabelClick} disabled={saving}>
            {saving ? "Fetching..." : "Print Label"}
          </Button>
        )}

        {order?.trackingId && order?.status !== "CANCELLED" && order?.status !== "DELIVERED" && (
          <Button variant="dark" onClick={handleViewTracking} disabled={fetchingTracking || saving}>
            {fetchingTracking ? "Loading..." : "View Tracking"}
          </Button>
        )}

        { (order?.status !== "DELIVERED" || role === 'superadmin') && (
          <Button variant="primary" onClick={handleStatusUpdate} disabled={saving || error}>
            {saving ? "Updating..." : "Update Status"}
          </Button>
        )}
      
      </Modal.Footer>
      <LabelSelectionModal
        show={showLabelModal}
        onHide={() => setShowLabelModal(false)}
        onSelect={handlePrintLabel}
        options={labelOptions}
      />
      <TrackingDetailsModal
        show={showTrackingModal}
        onHide={() => setShowTrackingModal(false)}
        trackingData={trackingData}
      />
      {showAddressModal && order && (
        <UpdateOrderAddressModal
          show={showAddressModal}
          onHide={() => setShowAddressModal(false)}
          order={order}
          onSuccess={(updatedOrder) => {
            setOrder((prev) => ({ ...prev, ...updatedOrder }));
            if (onOrderUpdate) onOrderUpdate();
          }}
        />
      )}
    </Modal>
  );
};

const TrackingDetailsModal = ({ show, onHide, trackingData }) => {
  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>DTDC Tracking Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {trackingData && trackingData.status === "SUCCESS" ? (
          <div>
            <div className="mb-4 p-3 bg-light rounded border">
              <div className="row">
                <div className="col-md-6">
                  <p className="mb-1"><strong>AWB Number:</strong> {trackingData.trackHeader?.strcnno}</p>
                  <p className="mb-1"><strong>Status:</strong> <span className="badge bg-primary">{trackingData.trackHeader?.strStatus}</span></p>
                </div>
                <div className="col-md-6">
                  <p className="mb-1"><strong>Origin:</strong> {trackingData.trackHeader?.strOrigin}</p>
                  <p className="mb-1"><strong>Destination:</strong> {trackingData.trackHeader?.strDest}</p>
                </div>
              </div>
            </div>

            <h6 className="fw-bold mb-3">Tracking History</h6>
            <div className="table-responsive">
              <Table striped bordered hover size="sm">
                <thead className="table-light">
                  <tr>
                    <th>Date</th>
                    <th>Time</th>
                    <th>Location</th>
                    <th>Activity</th>
                  </tr>
                </thead>
                <tbody>
                  {trackingData.trackDetails && trackingData.trackDetails.length > 0 ? (
                    trackingData.trackDetails.map((detail, index) => (
                      <tr key={index}>
                        <td>{detail.strActionDate}</td>
                        <td>{detail.strActionTime}</td>
                        <td>{detail.strLocation}</td>
                        <td>{detail.strAction}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center">No history available</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        ) : (
          <p className="text-center text-muted py-4">
            {trackingData?.message || "No tracking information found for this AWB."}
          </p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
};

const LabelSelectionModal = ({ show, onHide, onSelect, options }) => {
  const [selectedLabel, setSelectedLabel] = useState(options[0].value);

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Select Label Format</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group>
          <Form.Label>Choose a format for the shipping label:</Form.Label>
          <Form.Select 
            value={selectedLabel} 
            onChange={(e) => setSelectedLabel(e.target.value)}
          >
            {options.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </Form.Select>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Cancel</Button>
        <Button variant="primary" onClick={() => onSelect(selectedLabel)}>Print</Button>
      </Modal.Footer>
    </Modal>
  );
};
export default OrderDetails;